import { Toast as BaseToast } from "@base-ui/react/toast";
import { useMemo, type ReactNode } from "react";
import { cx, usePortalContainer } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";

export type ToastTone = "info" | "success" | "warning" | "danger";

const toneIcon: Record<ToastTone, IconName> = { info: "info", success: "check-circle", warning: "warning", danger: "warning" };

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** ms before auto-dismiss; 0 keeps it until dismissed. Default 5000. */
  timeout?: number;
  /** "high" announces assertively (role=alert). */
  priority?: "low" | "high";
}

export interface ToastProviderProps {
  children?: ReactNode;
  /** Default timeout, ms. */
  timeout?: number;
  /** Maximum toasts on screen. */
  limit?: number;
}

/** Put once inside <PresentationRoot>; then call useToast() anywhere below. */
export function ToastProvider({ children, timeout = 5000, limit = 3 }: ToastProviderProps) {
  const container = usePortalContainer();
  return (
    <BaseToast.Provider timeout={timeout} limit={limit}>
      {children}
      <BaseToast.Portal container={container}>
        <BaseToast.Viewport className="pmu-toast-viewport">
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  );
}

function ToastList() {
  const { toasts } = BaseToast.useToastManager();
  return toasts.map((t) => {
    const tone = (t.type ?? "info") as ToastTone;
    return (
      <BaseToast.Root key={t.id} toast={t} className={cx("pmu-toast", `pmu-toast--${tone}`)}>
        <BaseToast.Content className="pmu-toast__content">
          <Icon name={toneIcon[tone] ?? "info"} className="pmu-toast__icon" />
          <div className="pmu-toast__text">
            <BaseToast.Title className="pmu-toast__title" />
            <BaseToast.Description className="pmu-toast__description" />
          </div>
          <BaseToast.Close className="pmu-toast__close" aria-label="Dismiss"><Icon name="close" /></BaseToast.Close>
        </BaseToast.Content>
      </BaseToast.Root>
    );
  });
}

export interface ToastApi {
  show: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

export function useToast(): ToastApi {
  const manager = BaseToast.useToastManager();
  return useMemo(() => ({
    show: ({ tone = "info", ...o }: ToastOptions) => manager.add({ ...o, type: tone }),
    dismiss: (id: string) => manager.close(id),
  }), [manager]);
}
